import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';

const Analytics = typeof VercelAnalytics === 'function' || (typeof VercelAnalytics === 'object' && VercelAnalytics !== null) ? VercelAnalytics : () => null;

// Always-loaded layout components (small, needed on every page)
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import NotificationModal from "./components/NotificationModal";
import PendingHavaleResumeModal from "./components/PendingHavaleResumeModal";
import ScrollUpButton from "./components/ScrollUpButton";
import GoogleAnalytics from "./utils/analytics";

// ─── Lazy-loaded pages ─────────────────────────────────────────────────────
// Each page is loaded only when the user navigates to it, reducing initial bundle size.
const Home = lazy(() => import("./pages/Home"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const About = lazy(() => import("./pages/About"));
const CartPage = lazy(() => import("./pages/CartPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const Contact = lazy(() => import("./pages/Contact"));
const LiveMarkets = lazy(() => import("./pages/LiveMarkets"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const SSS = lazy(() => import("./pages/SSS"));
const VideoTutorials = lazy(() => import("./pages/VideoTutorials"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const LoginRedirect = lazy(() => import("./pages/LoginRedirect"));
const OrderError = lazy(() => import("./pages/OrderError"));

// Legal pages (rarely visited — high priority for lazy loading)
const KVKKAydinlatma = lazy(() => import("./pages/legal/KVKKAydinlatma"));
const AcikRiza = lazy(() => import("./pages/legal/AcikRiza"));
const GizlilikPolitikasi = lazy(() => import("./pages/legal/GizlilikPolitikasi"));
const CerezPolitikasi = lazy(() => import("./pages/legal/CerezPolitikasi"));
const KullanimKosullari = lazy(() => import("./pages/legal/KullanimKosullari"));
const IadeIptalKosullari = lazy(() => import("./pages/legal/IadeIptalKosullari"));
const MesafeliSatisSozlesmesi = lazy(() => import("./pages/legal/MesafeliSatisSozlesmesi"));
const OnBilgilendirmeFormu = lazy(() => import("./pages/legal/OnBilgilendirmeFormu"));

// ─── Page loading fallback ─────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-[#D4AF37]/20" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#D4AF37] animate-spin" />
        </div>
        <p className="text-[#D4AF37] font-['Cormorant_Garamond'] italic text-sm tracking-widest">
          Yükleniyor...
        </p>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const authPaths = ["/sifremi-unuttum", "/sifre-sifirla", "/email-dogrula"];
  const isAuthPage = authPaths.includes(location.pathname);

  // Scroll To Top on route change and guarantee body scroll is unlocked
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = '';
    document.body.style.position = '';
  }, [location.pathname]);



  // Global Scroll Lock for ALL modals/overlays (iOS Safari compatible)
  useEffect(() => {
    let wasLocked = false;

    const lockScroll = () => {
      if (!wasLocked) {
        document.body.style.overflow = 'hidden';
        wasLocked = true;
      }
    };

    const unlockScroll = () => {
      if (wasLocked) {
        document.body.style.overflow = '';
        wasLocked = false;
      }
    };

    const checkModal = () => {
      // Sadece data-scroll-lock="true" özelliğine sahip elemanları yakala (çok daha güvenli)
      const hasLockOverlay = document.querySelector('[data-scroll-lock="true"]');
      if (hasLockOverlay) {
        lockScroll();
      } else {
        unlockScroll();
      }
    };

    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ['data-scroll-lock'],
      subtree: false // Sadece body'nin direkt children'ını izle
    });

    checkModal();

    return () => {
      observer.disconnect();
      unlockScroll();
    };
  }, []);

  return (
    <div className="App min-h-screen flex flex-col">
      <GoogleAnalytics />
      {!isAuthPage && <Header />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/arama" element={<SearchResults />} />
            <Route path="/urun/:slug" element={<ProductDetail />} />
            <Route path="/sarrafiye" element={<CategoryPage />} />
            <Route path="/sarrafiye/:category" element={<CategoryPage />} />
            <Route path="/sarrafiye/:category/:subcategory" element={<CategoryPage />} />
            <Route path="/taki-ve-mucevherat" element={<CategoryPage />} />
            <Route path="/taki-ve-mucevherat/:slug" element={<CategoryPage />} />
            <Route path="/taki-ve-mucevherat/:category/:subcategory" element={<CategoryPage />} />
            <Route path="/kategori/:category/:subcategory" element={<CategoryPage />} />
            <Route path="/kategori/:slug" element={<CategoryPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/hakkimizda" element={<About />} />
            <Route path="/sepet" element={<CartPage />} />
            <Route path="/siparis-takibi" element={<OrderTracking />} />
            <Route path="/cok-satanlar" element={<CategoryPage />} />
            <Route path="/canli-piyasalar" element={<LiveMarkets />} />
            <Route path="/iletisim" element={<Contact />} />
            <Route path="/odeme" element={<Checkout />} />
            <Route path="/sss" element={<SSS />} />
            <Route path="/video-egitimleri" element={<VideoTutorials />} />
            <Route path="/siparis-teyit" element={<OrderSuccess />} />
            <Route path="/siparis-basarili" element={<OrderSuccess />} />
            <Route path="/siparis-hata" element={<OrderError />} />

            {/* Auth — tek giriş noktası (LoginModal) */}
            <Route path="/login" element={<LoginRedirect />} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route path="/email-dogrula" element={<VerifyEmail />} />

            {/* Legal Pages */}
            <Route path="/legal/kvkk-aydinlatma" element={<KVKKAydinlatma />} />
            <Route path="/legal/acik-riza" element={<AcikRiza />} />
            <Route path="/legal/gizlilik-politikasi" element={<GizlilikPolitikasi />} />
            <Route path="/legal/cerez-politikasi" element={<CerezPolitikasi />} />
            <Route path="/legal/kullanim-kosullari" element={<KullanimKosullari />} />
            <Route path="/legal/iade-iptal-kosullari" element={<IadeIptalKosullari />} />
            <Route path="/legal/mesafeli-satis-sozlesmesi" element={<MesafeliSatisSozlesmesi />} />
            <Route path="/legal/on-bilgilendirme-formu" element={<OnBilgilendirmeFormu />} />

            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isAuthPage && <Footer />}
      {!isAuthPage && <CookieConsent />}
      {!isAuthPage && <NotificationModal />}
      {!isAuthPage && <PendingHavaleResumeModal />}
      <ScrollUpButton />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CartProvider>
            <AppContent />
            <Analytics />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
